import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatCurrency, formatRelativeTime, getStatusBadgeClass, classNames } from '../lib/utils';
import {
  Server,
  Database,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import type { Account, Resource, Alert, CostSummaryItem } from '../lib/types';

export function DashboardPage() {
  const { idToken: accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [costSummary, setCostSummary] = useState<CostSummaryItem[]>([]);
  const [error, setError] = useState('');

  const fetchDashboardData = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');

    try {
      const [accountsRes, resourcesRes, alertsRes, costRes] = await Promise.allSettled([
        api.accounts.list(accessToken),
        api.resources.list(accessToken, { limit: 100 }),
        api.alerts.list(accessToken, { status: 'open' }),
        api.resources.costSummary(accessToken, { group_by: 'service', days: 30 }),
      ]);

      if (accountsRes.status === 'fulfilled') setAccounts(accountsRes.value.accounts);
      if (resourcesRes.status === 'fulfilled') setResources(resourcesRes.value.resources);
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.alerts);
      if (costRes.status === 'fulfilled') setCostSummary(costRes.value.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [accessToken]);

  const connectedAccounts = accounts.filter((a) => a.status === 'connected').length;
  const totalResources = resources.length;
  const runningResources = resources.filter((r) => r.state === 'running' || r.state === 'available').length;
  const openAlerts = alerts.filter((a) => a.status === 'open').length;
  const totalMonthlyCost = costSummary.reduce((sum, item) => sum + item.total_usd, 0);

  const stats = [
    {
      label: 'Connected Accounts',
      value: connectedAccounts,
      total: accounts.length,
      icon: Server,
      color: 'var(--color-primary)',
      trend: connectedAccounts === accounts.length ? 'up' : 'down',
      trendLabel: `${connectedAccounts}/${accounts.length} connected`,
    },
    {
      label: 'Total Resources',
      value: totalResources,
      icon: Database,
      color: 'var(--clr-teal)',
      trend: 'up',
      trendLabel: `${runningResources} running`,
    },
    {
      label: 'Monthly Cost (30d)',
      value: formatCurrency(totalMonthlyCost),
      icon: DollarSign,
      color: 'var(--color-amber)',
      trend: 'up',
      trendLabel: 'Last 30 days',
    },
    {
      label: 'Open Alerts',
      value: openAlerts,
      icon: AlertTriangle,
      color: openAlerts > 0 ? 'var(--color-red)' : 'var(--color-primary)',
      trend: openAlerts > 0 ? 'up' : 'down',
      trendLabel: openAlerts > 0 ? 'Needs attention' : 'All clear',
    },
  ];

  if (isLoading) {
    return (
      <div className="page-dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Dashboard</h1>
        </div>
        <div className="dashboard-stats">
          {stats.map(() => (
            <div key={Math.random()} className="card stat-card skeleton-loading">
              <div className="skeleton" style={{ height: 48, width: '60%', borderRadius: 'var(--radius-md)' }} />
              <div className="skeleton" style={{ height: 24, width: '40%', borderRadius: 'var(--radius-md)', marginTop: 'var(--space-2)' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-dashboard">
        <div className="empty-state">
          <AlertTriangle size={64} className="empty-state-icon" />
          <h3 className="empty-state-title">Failed to load dashboard</h3>
          <p className="empty-state-text">{error}</p>
          <button className="btn btn-primary" onClick={fetchDashboardData} style={{ marginTop: 'var(--space-4)' }}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-dashboard">
      <div className="dashboard-header">
        <h1 className="page-title">Dashboard</h1>
        <div className="dashboard-header-actions">
          <button className="btn btn-ghost btn-icon" onClick={fetchDashboardData} aria-label="Refresh data" disabled={isLoading}>
            <RefreshCw size={20} className={isLoading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-stats" role="list" aria-label="Key metrics">
        {stats.map((stat, index) => (
          <article key={index} className="card stat-card" role="listitem">
            <div className="stat-card-header">
              <div className="stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                <stat.icon size={24} aria-hidden="true" />
              </div>
              <span className={classNames('stat-trend', { up: stat.trend === 'up', down: stat.trend === 'down' })}>
                {stat.trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {stat.trendLabel}
              </span>
            </div>
            <div className="stat-card-body">
              <p className="stat-label">{stat.label}</p>
              <p className="stat-value">{stat.value}</p>
              {stat.total !== undefined && (
                <p className="stat-sub">{stat.total} total</p>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* Quick Views Grid */}
      <div className="dashboard-grid">
        {/* Recent Alerts */}
        <section className="card" aria-labelledby="alerts-heading">
          <header className="card-header">
            <h2 id="alerts-heading" className="card-title">Recent Alerts</h2>
            <a href="/alerts" className="btn btn-ghost btn-sm">View all</a>
          </header>
          <div className="card-content">
            {alerts.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                <CheckCircle size={48} className="empty-state-icon" style={{ color: 'var(--color-primary)' }} />
                <h3 className="empty-state-title">No open alerts</h3>
                <p className="empty-state-text">All systems operating normally</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Alert</th>
                      <th>Account</th>
                      <th>Metric</th>
                      <th>Value</th>
                      <th>Triggered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.slice(0, 5).map((alert) => (
                      <tr key={alert.alert_id}>
                        <td>
                          <span className={classNames('badge', `badge-${getStatusBadgeClass(alert.status)}`)}>
                            {alert.status}
                          </span>
                        </td>
                        <td>
                          <code style={{ fontSize: 'var(--text-xs)' }}>{alert.account_id}</code>
                        </td>
                        <td>{alert.metric}</td>
                        <td>
                          <span className="font-mono">{alert.observed_value} (threshold: {alert.threshold})</span>
                        </td>
                        <td>{formatRelativeTime(alert.triggered_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Cost Breakdown */}
        <section className="card" aria-labelledby="cost-heading">
          <header className="card-header">
            <h2 id="cost-heading" className="card-title">Top Services by Cost (30d)</h2>
            <a href="/costs" className="btn btn-ghost btn-sm">View all</a>
          </header>
          <div className="card-content">
            {costSummary.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                <DollarSign size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No cost data</h3>
                <p className="empty-state-text">Cost data will appear after the first daily sync</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {costSummary.slice(0, 5).map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--slate-50)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <span className="badge badge-info">{index + 1}</span>
                      <span style={{ fontWeight: 500 }}>{item.key}</span>
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--slate-900)' }}>
                      {formatCurrency(item.total_usd)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Account Status */}
        <section className="card" aria-labelledby="accounts-heading">
          <header className="card-header">
            <h2 id="accounts-heading" className="card-title">Account Status</h2>
            <a href="/accounts" className="btn btn-ghost btn-sm">Manage</a>
          </header>
          <div className="card-content">
            {accounts.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                <Server size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No accounts connected</h3>
                <p className="empty-state-text">Add your first AWS account to start monitoring</p>
                <button className="btn btn-primary" style={{ marginTop: 'var(--space-4)' }}>
                  Add Account
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {accounts.slice(0, 5).map((account) => (
                  <div key={account.account_id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--slate-50)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <span className={classNames('status-dot', account.status)} />
                      <div>
                        <p style={{ fontWeight: 600 }}>{account.alias}</p>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>
                          {account.account_id} • {account.enabled_regions.length} region(s)
                        </p>
                      </div>
                    </div>
                    <span className={classNames('badge', `badge-${getStatusBadgeClass(account.status)}`)}>
                      {account.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Resource Types Distribution */}
        <section className="card" aria-labelledby="resources-heading">
          <header className="card-header">
            <h2 id="resources-heading" className="card-title">Resource Types</h2>
            <a href="/resources" className="btn btn-ghost btn-sm">View all</a>
          </header>
          <div className="card-content">
            {resources.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                <Database size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No resources found</h3>
                <p className="empty-state-text">Run a scan to discover resources</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {Object.entries(
                  resources.reduce((acc, r) => {
                    acc[r.resource_type] = (acc[r.resource_type] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>)
                ).map(([type, count]) => (
                  <div
                    key={type}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--slate-50)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <span className="badge badge-info">{count}</span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                      {type.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}